import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { es } from "date-fns/locale";
import { format } from "date-fns";

import ManropeSemiBold from "../../assets/fonts/Manrope-SemiBold.ttf";
import ManropeRegular from "../../assets/fonts/Manrope-Regular.ttf";
import ManropeMedium from "../../assets/fonts/Manrope-Medium.ttf";

import Header from "../../assets/images/header_pdf.png";

import { capitalizeFirst } from "../../utils/capitalize";
import { Row } from "./DashboardUtils";

import { HospitalItem } from "../Header/HeaderUtils";

// A S S E T S
import LogoAltaEspecialidad from "../../assets/images/logo_alta_especialidad.png";
import LogoDefault from "../../assets/images/logo_base_muguerza.png";
import LogoSaltillo from "../../assets/images/logo_saltillo.png";
import LogoConchita from "../../assets/images/logo_conchita.svg";
import LogoCumbres from "../../assets/images/logo_cumbres.png";

export const getImageByHospital = (hospital: HospitalItem) => {
	switch (hospital.name) {
		case "Cumbres":
			return LogoCumbres;
		case "Saltillo":
			return LogoSaltillo;
		case "Alta Especialidad":
			return LogoAltaEspecialidad;
		case "Conchita":
			return LogoConchita;
		default:
			return LogoDefault;
	}
};

Font.register({
	family: "Manrope",
	fonts: [
		{
			src: ManropeRegular,
			fontWeight: 400,
		},
		{
			src: ManropeMedium,
			fontWeight: 500,
		},
		{
			src: ManropeSemiBold,
			fontWeight: 600,
		},
	],
});

const styles = StyleSheet.create({
	viewer: {
		width: window.innerWidth,
		height: window.innerHeight,
	},
	header: {
		position: "absolute",
		left: "0px",
		right: "0px",
		marginHorizontal: "auto",
		textAlign: "center",
		justifyContent: "center",
	},
	header__image: {
		width: "5rem",
		top: "0.9rem",
		left: "1rem",
	},
	header__line: {
		width: "1px",
		height: "2.7rem",
		backgroundColor: "#ffffff",
		opacity: 0.3,
		position: "absolute",
		top: "0.6rem",
		left: "6.5rem",
		borderRadius: "10px",
	},
	header__line__variant: {
		width: "1px",
		height: "1rem",
		backgroundColor: "#ffffff",
		opacity: 0.3,
		position: "absolute",
		top: "1.5rem",
		right: "2.5rem",
		borderRadius: "10px",
	},
	header__schedule: {
		position: "absolute",
		top: "1.1rem",
		left: "7.2rem",
	},
	header__title: {
		fontFamily: "Manrope",
		color: "#FFFFFF",
		fontWeight: 500,
		fontSize: "11px",
	},
	header__date: {
		fontFamily: "Manrope",
		color: "#FFFFFF",
		position: "absolute",
		right: "3rem",
		fontSize: "11px",
		fontWeight: 600,
		top: "1.6rem",
	},
	header__page: {
		fontFamily: "Manrope",
		color: "#FFFFFF",
		position: "absolute",
		right: "1rem",
		fontSize: "11px",
		fontWeight: 600,
		top: "1.6rem",
	},
	table: {
		width: "41.3rem",
		border: "1px solid #dcdcdc",
		borderRadius: "3px",
		marginTop: "15px",
		marginBottom: "10px",
		marginHorizontal: "25px",
	},
	table__row: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		paddingLeft: "15px",
		paddingTop: "5px",
		paddingBottom: "5px",
		borderBottom: "1px solid #dcdcdc",
	},
	table__header__title: {
		fontFamily: "Manrope",
		fontWeight: 600,
		fontSize: "11px",
		color: "#6d247a",
	},
	table__row__info: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		fontFamily: "Manrope",
		fontWeight: 400,
		fontSize: "11px",
		color: "#292929",
	},
	table__row__info__empty: {
		fontFamily: "Manrope",
		fontWeight: 400,
		fontSize: "11px",
		color: "#989898",
		display: "flex",
	},
	table__col1: {
		width: "15%",
	},
	table__col2: {
		width: "15%",
	},
	table__col3: {
		width: "35%",
		display: "flex",
		alignItems: "center",
		flexDirection: "row",
	},
	table__col4: {
		width: "35%",
		display: "flex",
		flexDirection: "row",
	},
	pill: {
		borderRadius: "25px",
		fontFamily: "Manrope",
		fontWeight: 600,
		fontSize: "8px",
		color: "#FFFFFF",
		paddingHorizontal: "8px",
		paddingVertical: "1.6px",
		marginRight: "5px",
	},
	pill__purple: {
		backgroundColor: "#6d247a",
	},
	pill__purple__variant: {
		backgroundColor: "#f5eff6",
		color: "#6d247a",
	},
	pill__blue: {
		backgroundColor: "#3f6a98",
	},
	pill__blue__variant: {
		backgroundColor: "#f4f7fb",
		color: "#3f6a98",
	},
	pill__green: {
		backgroundColor: "#489f4a",
	},
	pill__green__variant: {
		backgroundColor: "#f3faf3",
		color: "#378239",
	},
	pill__yellow: {
		backgroundColor: "#f1cc68",
		color: "#401908",
	},
	pill__yellow__variant: {
		backgroundColor: "#faefcb",
		color: "#ca7015",
	},
	pill__rose: {
		backgroundColor: "#e58c6d",
		color: "#3e1d11",
	},
	pill__rose__variant: {
		backgroundColor: "#fbebe5",
		color: "#c75f3b",
	},
	pill__gray: {
		backgroundColor: "#989898",
		color: "#000000",
	},
	pill__gray__variant: {
		backgroundColor: "#eeeeee",
		color: "#656565",
	},
	pill__empty: {
		display: "none",
	},
});

const getPillNurse = (classX: string) => {
	const pills = {
		"ENF A": styles.pill__purple,
		"ENF B": styles.pill__blue,
		"ENF C": styles.pill__green,
		"ENF D": styles.pill__yellow__variant,
		COORD: styles.pill__yellow,
		"ENF ESP": styles.pill__rose,
		"AUX A": styles.pill__purple__variant,
		"AUX B": styles.pill__blue__variant,
		"AUX C": styles.pill__green__variant,
		AUX: styles.pill__rose__variant,
		"ENF AUX": styles.pill__gray,
		"ENF SUB": styles.pill__gray__variant,
		"": styles.pill__empty,
	};

	return (
		<View style={[styles.pill, pills[classX as keyof typeof pills]]}>
			<Text>{classX}</Text>
		</View>
	);
};

const groupItemsPerPage = (items: Row[], itemsPerPage: number) => {
	const grouped = [];

	for (let i = 0; i < items.length; i += itemsPerPage) {
		grouped.push(items.slice(i, i + itemsPerPage));
	}

	return grouped;
};

const getNurseLabel = (nurseName: string): string => {
	return nurseName !== "" ? nurseName : "Sin profesional asignado";
};

const getNurseLabelStyle = (nurseName: string) => {
	return nurseName !== "" ? styles.table__row__info : styles.table__row__info__empty;
};

export const DashboardPDF = ({
	fileName,
	area,
	schedule,
	imageURL,
	data,
}: {
	fileName: string;
	area: string;
	schedule: string;
	imageURL: string;
	data: Row[];
}) => {
	const pagesContent = groupItemsPerPage(data, 18);

	return (
		<Document title={fileName}>
			{pagesContent.map((pageItems: Row[], pageIndex) => {
				return (
					<Page
						size="LETTER"
						orientation="landscape"
						key={pageIndex}
					>
						<View>
							<Image src={Header} />
							<View style={styles.header}>
								<Image
									src={imageURL}
									style={styles.header__image}
								/>
								<View style={styles.header__line}></View>
								<View style={styles.header__schedule}>
									<Text style={styles.header__title}>{area}</Text>
									<Text style={styles.header__title}>{schedule}</Text>
								</View>
								<Text style={styles.header__date}>
									{capitalizeFirst(
										format(new Date(), "EEEE d 'de' MMMM 'de' yyyy", {
											locale: es,
										}),
									)}
								</Text>
								<View style={styles.header__line__variant}></View>
								<Text style={styles.header__page}>
									{pageIndex + 1}/{pagesContent.length}
								</Text>
							</View>
						</View>

						<View
							style={[styles.table, { height: `${pageItems.length + 1 * 1.5}rem` }]}
						>
							<View style={styles.table__row}>
								<Text style={[styles.table__header__title, styles.table__col1]}>
									Habitación
								</Text>
								<Text style={[styles.table__header__title, styles.table__col2]}>
									Categoría
								</Text>
								<Text style={[styles.table__header__title, styles.table__col3]}>
									Profesional
								</Text>
								<Text style={[styles.table__header__title, styles.table__col4]}>
									Profesional Auxiliar
								</Text>
							</View>
							{pageItems.map((row: Row) => {
								return (
									<View style={styles.table__row}>
										<Text style={[styles.table__row__info, styles.table__col1]}>
											{row.room}
										</Text>
										<Text style={[styles.table__row__info, styles.table__col2]}>
											{row.category}
										</Text>
										<View style={styles.table__col3}>
											{getPillNurse(row.nurseClassx)}
											<Text style={getNurseLabelStyle(row.nurseName)}>
												{getNurseLabel(row.nurseName)}
											</Text>
										</View>
										<View style={styles.table__col4}>
											{getPillNurse(row.secondaryNurseClassx)}
											<Text
												style={getNurseLabelStyle(row.secondaryNurseName)}
											>
												{getNurseLabel(row.secondaryNurseName)}
											</Text>
										</View>
									</View>
								);
							})}
						</View>
					</Page>
				);
			})}
		</Document>
	);
};
